// Feature: features/nma-claims-command-center-insights.md, features/nma-claims-known-issues-tabs.md, features/nma-local-discovery-streamlit-console.md
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  HStack,
  VStack,
  Button,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  formatCurrency,
  normalizeAnalysisText,
  FormattedAnalysisText as SharedFormattedAnalysisText,
} from './shared';

const TwoPanelStageTrendCard = ({
  title,
  subtitleNode,
  weeklySeries,
  lineSpecs,
  barLabel,
  barColor,
  syncId,
  heightTop = 165,
  heightBottom = 95,
}) => (
  <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
    <CardBody p={3.5}>
      <Box mb={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.900">
          {title}
        </Text>
        {subtitleNode}
      </Box>

      {/* Top Panel: % Rate Line(s) (0 - 100%) */}
      <Box h={`${heightTop}px`} w="100%">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={weeklySeries} syncId={syncId} margin={{ top: 8, right: 12, left: -14, bottom: 2 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="week" hide />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 10, fill: '#1A202C' }}
              unit="%"
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: '#1A202C',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '11px',
                padding: '6px 10px',
              }}
              labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
              formatter={(val, name, props) => {
                const p = props?.payload || {};
                const spec = lineSpecs.find((s) => s.label === name);
                const numVal = spec ? p[spec.numKey] || 0 : 0;
                return [
                  val !== null && val !== undefined
                    ? `${val}% (${numVal.toLocaleString()} / ${(p.denom || 0).toLocaleString()} claims)`
                    : 'No volume',
                  name,
                ];
              }}
              labelFormatter={(wk) => `Week of ${wk}`}
            />
            {lineSpecs.length > 1 && <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '2px' }} />}
            {lineSpecs.map((spec) => (
              <Line
                key={spec.dataKey}
                type="monotone"
                dataKey={spec.dataKey}
                name={spec.label}
                stroke={spec.color}
                strokeWidth={2.3}
                strokeDasharray={spec.dash || undefined}
                dot={{ r: 3, fill: spec.color }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Bottom Panel: Weekly Denominator Claim Volume Bars Underneath */}
      <Box h={`${heightBottom}px`} w="100%" mt={1}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weeklySeries} syncId={syncId} margin={{ top: 2, right: 12, left: -14, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 9, fill: '#1A202C' }}
              angle={-30}
              textAnchor="end"
              interval={0}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: '#1A202C',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '11px',
                padding: '5px 8px',
              }}
              labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
              formatter={(val) => [`${Number(val || 0).toLocaleString()} claims`, barLabel]}
              labelFormatter={(wk) => `Week of ${wk}`}
            />
            <Bar
              dataKey="denom"
              name={barLabel}
              fill={barColor}
              stroke="#64748B"
              strokeWidth={0.8}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </CardBody>
  </Card>
);

const TwoPanelCohortFunnelCard = ({ weeklyFunnel }) => (
  <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
    <CardBody p={3.5}>
      <Box mb={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.900">
          4. Weekly Billed Cohort Status Funnel (By 837P Billed Week — 100% Stacked)
        </Text>
        <Text fontSize="11px" color="gray.600">
          Shows current lifecycle status of each weekly billing cohort (Resolved vs. Held vs. In-Flight Awaiting EOB)
        </Text>
      </Box>

      {/* Top Panel: 100% Stacked Status Bars */}
      <Box h="165px" w="100%">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weeklyFunnel} syncId="exec_s4_funnel" margin={{ top: 8, right: 12, left: -14, bottom: 2 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="week" hide />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 10, fill: '#1A202C' }}
              unit="%"
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: '#1A202C',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '11px',
                padding: '6px 10px',
              }}
              labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
              formatter={(val, name) => [`${val}%`, name]}
              labelFormatter={(wk) => `Week of ${wk}`}
            />
            <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '2px' }} />
            <Bar dataKey="paidFullPct" name="Paid Full" stackId="a" fill="#16A34A" isAnimationActive={false} />
            <Bar dataKey="partialPct" name="Partial Pay" stackId="a" fill="#F59E0B" isAnimationActive={false} />
            <Bar dataKey="zeroPayPct" name="Zero-Pay Denied" stackId="a" fill="#EA580C" isAnimationActive={false} />
            <Bar dataKey="recSentPct" name="Records Sent" stackId="a" fill="#2563EB" isAnimationActive={false} />
            <Bar dataKey="recUnsentPct" name="Waiting on Records" stackId="a" fill="#9333EA" isAnimationActive={false} />
            <Bar dataKey="chRejPct" name="277CA Rejected" stackId="a" fill="#DC2626" isAnimationActive={false} />
            <Bar dataKey="awaitingPct" name="In-Flight (Awaiting EOB)" stackId="a" fill="#94A3B8" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Bottom Panel: Weekly Claims Billed Volume */}
      <Box h="95px" w="100%" mt={1}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weeklyFunnel} syncId="exec_s4_funnel" margin={{ top: 2, right: 12, left: -14, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 9, fill: '#1A202C' }}
              angle={-30}
              textAnchor="end"
              interval={0}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: '#1A202C',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '11px',
                padding: '5px 8px',
              }}
              labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
              formatter={(val) => [`${Number(val || 0).toLocaleString()} claims`, 'Claims Billed']}
              labelFormatter={(wk) => `Week of ${wk}`}
            />
            <Bar
              dataKey="denom"
              name="Claims Billed"
              fill="#CBD5E1"
              stroke="#64748B"
              strokeWidth={0.8}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </CardBody>
  </Card>
);

const NMAExecutiveSummaryTab = React.memo(({
  data,
  onStatusDrilldown,
  customAnalyses = [],
  onOpenCustomTask,
  onOpenAnalysisReport,
  FormattedAnalysisText = SharedFormattedAnalysisText,
}) => {
  const execData = data || {};
  const kpis = execData.kpis || {};
  const stage1ChTrend = execData.stage1_ch_trend || [];
  const stage2RecTrend = execData.stage2_rec_trend || [];
  const stage3EobTrend = execData.stage3_eob_trend || [];
  const stage4CohortFunnel = execData.stage4_cohort_funnel || [];
  const statusAgingRows = execData.status_aging_rows || [];
  const payerScorecardRows = execData.payer_scorecard_rows || [];
  const payerEobSmallMultiples = execData.payer_eob_small_multiples || [];

  return (
    <VStack spacing={6} align="stretch">
      {/* ================================================================
          SECTION 1: 4 CORE SUMMARY STAGE TREND GRAPHS (PRESET, NO TOGGLES)
         ================================================================ */}
      <Box>
        <Heading size="sm" color="gray.800" mb={2}>
          Weekly Stage Trends &amp; Cohort Funnel
        </Heading>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {/* Graph 1: Clearinghouse 277CA Rejections (By 837P Billed Week) */}
          <TwoPanelStageTrendCard
            title="1. Clearinghouse 277CA Rejection Rate (By 837P Billed Week)"
            subtitleNode={
              <Text fontSize="11px" color="gray.600">
                Denom = <b>Claims Billed that Week</b> &nbsp;|&nbsp; Overall Rejected:{' '}
                <Text as="span" color="red.600" fontWeight="bold">
                  {kpis.ch_rej_pct ?? 0}%
                </Text>{' '}
                ({(kpis.ch_rej_cnt || 0).toLocaleString()} / {(kpis.total_claims || 0).toLocaleString()})
              </Text>
            }
            weeklySeries={stage1ChTrend}
            lineSpecs={[
              { dataKey: 'ratePct', numKey: 'num', label: '277CA Rejection %', color: '#DC2626' },
            ]}
            barLabel="Claims Billed"
            barColor="#CBD5E1"
            syncId="exec_s1_ch"
          />

          {/* Graph 2: Pre-Pay Medical Record Holds (By 837P Billed Week) */}
          <TwoPanelStageTrendCard
            title="2. Pre-Pay Medical Record Holds (By 837P Billed Week)"
            subtitleNode={
              <Text fontSize="11px" color="gray.600">
                Denom = <b>CH-Accepted Claims</b> &nbsp;|&nbsp; Ever Held:{' '}
                <Text as="span" color="purple.600" fontWeight="bold">
                  {kpis.rec_any_pct ?? 0}%
                </Text>{' '}
                ({(kpis.rec_any_cnt || 0).toLocaleString()}) &nbsp;|&nbsp; Unsent Backlog:{' '}
                <Text as="span" color="red.600" fontWeight="bold">
                  {kpis.rec_unsent_pct ?? 0}%
                </Text>{' '}
                ({(kpis.rec_waiting_cnt || 0).toLocaleString()})
              </Text>
            }
            weeklySeries={stage2RecTrend}
            lineSpecs={[
              { dataKey: 'ratePct', numKey: 'num', label: 'Total Triggered Record Hold %', color: '#7C3AED' },
              { dataKey: 'rate2Pct', numKey: 'num2', label: 'Waiting on Records (Unsent) %', color: '#DC2626' },
              { dataKey: 'rate3Pct', numKey: 'num3', label: 'Records Sent (Awaiting Payer) %', color: '#2563EB', dash: '4 4' },
            ]}
            barLabel="CH-Accepted Claims"
            barColor="#DDD6FE"
            syncId="exec_s2_rec"
          />

          {/* Graph 3: EOB Adjudication Outcomes (By EOB Remittance Week, Denom = Finalized EOBs) */}
          <TwoPanelStageTrendCard
            title="3. EOB Adjudication Outcomes (By EOB Remittance Week, Denom = Finalized EOBs)"
            subtitleNode={
              <Text fontSize="11px" color="gray.600">
                <Text as="span" color="green.600" fontWeight="bold">
                  ● Paid Full: {kpis.paid_full_pct ?? 0}%
                </Text>{' '}
                ({(kpis.eob_paid_full_cnt || 0).toLocaleString()}) &nbsp;|&nbsp;{' '}
                <Text as="span" color="orange.600" fontWeight="bold">
                  ● Partial w/ Denials: {kpis.part_den_pct ?? 0}%
                </Text>{' '}
                ({(kpis.eob_part_den_cnt || 0).toLocaleString()}) &nbsp;|&nbsp;{' '}
                <Text as="span" color="red.600" fontWeight="bold">
                  ● Zero-Pay Denied: {kpis.zero_den_pct ?? 0}%
                </Text>{' '}
                ({(kpis.eob_zero_den_cnt || 0).toLocaleString()})
              </Text>
            }
            weeklySeries={stage3EobTrend}
            lineSpecs={[
              { dataKey: 'ratePct', numKey: 'num', label: 'Paid in Full / Deductible %', color: '#16A34A' },
              { dataKey: 'rate2Pct', numKey: 'num2', label: 'Partial Pay w/ Line Denials %', color: '#D97706' },
              { dataKey: 'rate3Pct', numKey: 'num3', label: 'Zero-Pay Denied ($0) %', color: '#DC2626' },
            ]}
            barLabel="Finalized EOBs Received"
            barColor="#FED7AA"
            syncId="exec_s3_eob"
          />

          {/* Graph 4: Weekly Billed Cohort Status Funnel */}
          <TwoPanelCohortFunnelCard weeklyFunnel={stage4CohortFunnel} />
        </SimpleGrid>
      </Box>

      {/* ================================================================
          SECTION 2: CLAIMS AGING BY STATUS & FINANCIAL EXPOSURE (BETA)
         ================================================================ */}
      <Box>
        <Heading size="sm" color="gray.800" mb={2}>
          Claims Aging by Status (Days in Current Status) &amp; Financial Exposure (beta)
        </Heading>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={4}>
          {/* Left Chart: Workqueue Aging by Status */}
          <Card variant="outline" bg="white">
            <CardHeader pb={1} pt={3} px={4}>
              <Text fontSize="xs" fontWeight="bold" color="gray.800">
                A. Workqueue Aging by Status (Days in Current Status: 0–14d to 60+d)
              </Text>
            </CardHeader>
            <CardBody pt={1} px={3} pb={3}>
              <Box h="295px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={statusAgingRows}
                    layout="vertical"
                    margin={{ top: 6, right: 16, left: 10, bottom: 6 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                    <YAxis
                      type="category"
                      dataKey="short_label"
                      width={155}
                      tick={{ fontSize: 11, fill: '#1A202C', fontWeight: 600 }}
                    />
                    <RechartsTooltip
                      formatter={(val, name) => [`${Number(val || 0).toLocaleString()} claims`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="age_0_14d" name="0–14d" stackId="age" fill="#10B981" isAnimationActive={false} />
                    <Bar dataKey="age_15_30d" name="15–30d" stackId="age" fill="#3B82F6" isAnimationActive={false} />
                    <Bar dataKey="age_31_45d" name="31–45d" stackId="age" fill="#F59E0B" isAnimationActive={false} />
                    <Bar dataKey="age_46_60d" name="46–60d" stackId="age" fill="#EA580C" isAnimationActive={false} />
                    <Bar dataKey="age_60plus_d" name="60+d" stackId="age" fill="#DC2626" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          {/* Right Chart: Financial Exposure (beta) by Status */}
          <Card variant="outline" bg="white">
            <CardHeader pb={1} pt={3} px={4}>
              <Text fontSize="xs" fontWeight="bold" color="gray.800">
                B. Financial Exposure (beta) by Status (Denied $, Paid $, Billed $)
              </Text>
            </CardHeader>
            <CardBody pt={1} px={3} pb={3}>
              <Box h="295px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={statusAgingRows}
                    layout="vertical"
                    margin={{ top: 6, right: 16, left: 10, bottom: 6 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: '#1A202C' }}
                      tickFormatter={(v) => `$${(Number(v || 0) / 1000000).toFixed(1)}M`}
                    />
                    <YAxis
                      type="category"
                      dataKey="short_label"
                      width={155}
                      tick={{ fontSize: 11, fill: '#1A202C', fontWeight: 600 }}
                    />
                    <RechartsTooltip
                      formatter={(val, name) => [formatCurrency(val), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="denied" name="Total Denied $ (beta)" fill="#DC2626" isAnimationActive={false} />
                    <Bar dataKey="paid" name="Total Paid $ (beta)" fill="#16A34A" isAnimationActive={false} />
                    <Bar dataKey="billed" name="Total Billed $ (beta)" fill="#94A3B8" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Status x Workqueue Aging x Unblurred Financials (beta) Table */}
        <Card variant="outline" bg="white">
          <CardBody p={0}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Operational Status</Th>
                    <Th>Clock Anchor</Th>
                    <Th isNumeric>Claims (Pro/Tech)</Th>
                    <Th isNumeric>Share %</Th>
                    <Th isNumeric color="green.700">0–14d</Th>
                    <Th isNumeric color="blue.700">15–30d</Th>
                    <Th isNumeric color="orange.700">31–45d</Th>
                    <Th isNumeric color="orange.800">46–60d</Th>
                    <Th isNumeric color="red.700">60+d</Th>
                    <Th isNumeric>Med Stage Age</Th>
                    <Th isNumeric>Med Billed Age</Th>
                    <Th isNumeric>Med DOS Age</Th>
                    <Th isNumeric>Billed $ (beta)</Th>
                    <Th isNumeric>Paid $ (beta)</Th>
                    <Th isNumeric>Denied $ (beta)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {statusAgingRows.map((r) => (
                    <Tr
                      key={r.op_status}
                      _hover={{ bg: 'blue.50' }}
                      cursor="pointer"
                      onClick={() => onStatusDrilldown && onStatusDrilldown(r.quick_filter)}
                      title="Click to open filtered Claims Worklist (Tab 3)"
                    >
                      <Td fontWeight="bold" fontSize="xs" color="blue.700">
                        {r.op_status}
                      </Td>
                      <Td fontSize="11px" color="gray.600">
                        {r.clock_anchor}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="semibold">
                        {(r.claims || 0).toLocaleString()} ({r.pro_claims}P / {r.tech_claims}T)
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold">
                        {r.share_pct}%
                      </Td>
                      <Td isNumeric fontSize="xs">{(r.age_0_14d || 0).toLocaleString()}</Td>
                      <Td isNumeric fontSize="xs">{(r.age_15_30d || 0).toLocaleString()}</Td>
                      <Td isNumeric fontSize="xs" fontWeight={r.age_31_45d > 0 ? 'semibold' : 'normal'}>
                        {(r.age_31_45d || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" color={r.age_46_60d > 0 ? 'orange.700' : 'gray.500'} fontWeight="semibold">
                        {(r.age_46_60d || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" color={r.age_60plus_d > 0 ? 'red.600' : 'gray.400'} fontWeight="bold">
                        {(r.age_60plus_d || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color="purple.700">
                        {r.med_stage_age}d
                      </Td>
                      <Td isNumeric fontSize="xs" color="gray.700">
                        {r.med_sub_age}d
                      </Td>
                      <Td isNumeric fontSize="xs" color="gray.600">
                        {r.med_dos_age}d
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="medium">
                        {formatCurrency(r.billed)}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color={r.paid > 0 ? 'green.600' : 'gray.400'}>
                        {formatCurrency(r.paid)}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color={r.denied > 0 ? 'red.600' : 'gray.400'}>
                        {formatCurrency(r.denied)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </Box>

      {/* ================================================================
          SECTION 3: PAYER GROUP PERFORMANCE SCORECARD
         ================================================================ */}
      <Box>
        <Heading size="sm" color="gray.800" mb={2}>
          Payer Group Performance Scorecard
        </Heading>
        <Card variant="outline" bg="white">
          <CardBody p={0}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Payer Group</Th>
                    <Th isNumeric>Billed Claims</Th>
                    <Th isNumeric>277CA Rej %</Th>
                    <Th isNumeric>Record Holds (Unsent / Sent)</Th>
                    <Th isNumeric>In-Flight (Awaiting EOB)</Th>
                    <Th isNumeric>Finalized EOBs (Denom)</Th>
                    <Th isNumeric color="green.700">EOB Paid Full %</Th>
                    <Th isNumeric color="orange.700">EOB Partial Pay %</Th>
                    <Th isNumeric color="red.700">EOB Zero-Pay Denied %</Th>
                    <Th isNumeric>Med Lag / Payer Speed</Th>
                    <Th isNumeric>Billed $ (beta)</Th>
                    <Th isNumeric>Paid $ (beta)</Th>
                    <Th isNumeric>Denied $ (beta)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payerScorecardRows.map((pg) => (
                    <Tr key={pg.payer_group} _hover={{ bg: 'gray.50' }}>
                      <Td fontWeight="bold" fontSize="xs">
                        {pg.payer_group}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold">
                        {(pg.billed_claims || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" color={pg.ch_rej_pct > 5 ? 'red.600' : 'gray.700'}>
                        {pg.ch_rej_pct}% ({pg.ch_rej_cnt})
                      </Td>
                      <Td isNumeric fontSize="xs">
                        <Text as="span" color={pg.rec_waiting_cnt > 0 ? 'purple.700' : 'gray.500'} fontWeight="bold">
                          {pg.rec_waiting_cnt}
                        </Text>{' '}
                        / {pg.rec_sent_cnt}
                      </Td>
                      <Td isNumeric fontSize="xs" color="gray.600">
                        {(pg.awaiting_eob_cnt || 0).toLocaleString()} ({pg.awaiting_eob_pct}%)
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="extrabold">
                        {(pg.finalized_eobs || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" color="green.600" fontWeight="bold">
                        {pg.eob_paid_full_pct !== null ? `${pg.eob_paid_full_pct}% (${pg.eob_paid_full_cnt})` : 'N/A'}
                      </Td>
                      <Td isNumeric fontSize="xs" color="orange.600" fontWeight="semibold">
                        {pg.eob_part_den_pct !== null ? `${pg.eob_part_den_pct}% (${pg.eob_part_den_cnt})` : 'N/A'}
                      </Td>
                      <Td isNumeric fontSize="xs" color="red.600" fontWeight="bold">
                        {pg.eob_zero_den_pct !== null ? `${pg.eob_zero_den_pct}% (${pg.eob_zero_den_cnt})` : 'N/A'}
                      </Td>
                      <Td isNumeric fontSize="xs" color="gray.700">
                        {pg.med_charge_lag ?? '-'}d / {pg.med_payer_adj ?? '-'}d
                      </Td>
                      <Td isNumeric fontSize="xs">
                        {formatCurrency(pg.billed)}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color={pg.paid > 0 ? 'green.600' : 'gray.400'}>
                        {formatCurrency(pg.paid)}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color={pg.denied > 0 ? 'red.600' : 'gray.400'}>
                        {formatCurrency(pg.denied)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </Box>

      {/* ================================================================
          SECTION 4: BY PAYER GROUP — WEEKLY EOB OUTCOME SMALL MULTIPLES
         ================================================================ */}
      {payerEobSmallMultiples.length > 0 && (
        <Box>
          <Heading size="sm" color="gray.800" mb={1}>
            Weekly EOB Adjudication Outcomes by Payer Group (By EOB Week)
          </Heading>
          <Text fontSize="xs" color="gray.600" mb={3}>
            Each Payer Group chart plots <b>🟢 Paid Full %</b>, <b>🟠 Partial Pay w/ Denials %</b>, and <b>🔴 Zero-Pay Denied %</b> (<b>sum = 100%</b>) above <b>Weekly Finalized EOB Volume</b> bars.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
            {payerEobSmallMultiples.map((pgItem, idx) => (
              <TwoPanelStageTrendCard
                key={pgItem.payer_group}
                title={`#${idx + 1} ${pgItem.payer_group} (${(pgItem.total_eobs || 0).toLocaleString()} Finalized EOBs)`}
                subtitleNode={
                  <Text fontSize="11px" color="gray.600">
                    <Text as="span" color="green.600" fontWeight="bold">
                      ● Full: {pgItem.paid_full_pct}%
                    </Text>{' '}
                    ({pgItem.paid_full_cnt}) |{' '}
                    <Text as="span" color="orange.600" fontWeight="bold">
                      ● Partial: {pgItem.partial_pct}%
                    </Text>{' '}
                    ({pgItem.partial_cnt}) |{' '}
                    <Text as="span" color="red.600" fontWeight="bold">
                      ● Zero-Pay: {pgItem.zero_pay_pct}%
                    </Text>{' '}
                    ({pgItem.zero_pay_cnt})
                  </Text>
                }
                weeklySeries={pgItem.weekly_series}
                lineSpecs={[
                  { dataKey: 'ratePct', numKey: 'num', label: 'Paid Full %', color: '#16A34A' },
                  { dataKey: 'rate2Pct', numKey: 'num2', label: 'Partial Pay %', color: '#D97706' },
                  { dataKey: 'rate3Pct', numKey: 'num3', label: 'Zero-Pay Denied %', color: '#DC2626' },
                ]}
                barLabel="Finalized EOBs"
                barColor="#CBD5E1"
                syncId={`exec_pg_${idx}`}
                heightTop={145}
                heightBottom={88}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Custom Payer Data Research Tasks (when present) */}
      {customAnalyses.length > 0 && (
        <Card variant="outline" borderColor="teal.200" bg="teal.50">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <HStack spacing={2}>
                <Heading size="sm" color="teal.800">
                  Custom Payer Data Research Tasks &amp; Reports
                </Heading>
                <Badge colorScheme="teal" variant="solid">
                  {customAnalyses.length}
                </Badge>
              </HStack>
              {onOpenCustomTask && (
                <Button size="xs" colorScheme="teal" leftIcon={<AddIcon />} onClick={onOpenCustomTask}>
                  New Research Task
                </Button>
              )}
            </Flex>
          </CardHeader>
          <CardBody pt={2}>
            <VStack spacing={2.5} align="stretch">
              {customAnalyses.map((ca, idx) => {
                const ar = ca.analysis_result || {};
                const isCompleted = Boolean(ca.id);
                const rawDate = ca.updated_at || ca.created_at || ca.analysis_job?.created_at || '';
                const formattedDate = rawDate ? `${String(rawDate).replace('T', ' ').slice(0, 16)} UTC` : '';
                const objectiveText = normalizeAnalysisText(
                  ca.source_snapshot?.prompt || ca.source_snapshot?.title || ar.headline || ca.target_key
                );
                return (
                  <Card key={`${ca.target_key}-${idx}`} bg="white" shadow="xs" borderLeft="4px solid" borderLeftColor="teal.500">
                    <CardBody py={3} px={4}>
                      <Flex justify="space-between" align="center" gap={4} wrap="wrap">
                        <Box flex="1" minW="240px">
                          <HStack spacing={2} mb={1}>
                            <Badge colorScheme={isCompleted ? 'green' : 'orange'} fontSize="10px">
                              {isCompleted ? 'COMPLETED' : ca.analysis_job?.status?.toUpperCase() || 'PENDING'}
                            </Badge>
                            {formattedDate && (
                              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                {formattedDate}
                              </Text>
                            )}
                          </HStack>
                          <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                            {objectiveText}
                          </Text>
                        </Box>
                        {isCompleted && onOpenAnalysisReport && (
                          <Button
                            size="xs"
                            colorScheme="teal"
                            flexShrink={0}
                            onClick={() =>
                              onOpenAnalysisReport({
                                content_type: ca.content_type,
                                target_key: ca.target_key,
                                title: ca.source_snapshot?.title,
                                prompt: ca.source_snapshot?.prompt,
                                payer_name: ca.source_snapshot?.payer_name || 'Custom Research',
                                cpt_code: ca.source_snapshot?.cpt_code || 'ALL',
                                entity_refs: ca.entity_refs || {},
                                source_snapshot: ca.source_snapshot || {},
                                payer_data_analysis: ca,
                              })
                            }
                          >
                            View Research Report
                          </Button>
                        )}
                      </Flex>
                    </CardBody>
                  </Card>
                );
              })}
            </VStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
});

export default NMAExecutiveSummaryTab;
